import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import api from '../../api';
import { theme } from '../../theme';
import { CoworkCommandInfo, CoworkSettings } from '../../types/cowork';
import {
  Button,
  ErrorText,
  FormGrid,
  FormRow,
  Grid,
  Input,
  Label,
  Muted,
  Surface,
  SurfaceBody,
  SurfaceHeader,
  SurfaceTitle,
  Table,
  TableWrap,
  TextArea,
  Toast,
} from '../ui';

const Check = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.84rem;
  color: ${theme.colors.textMuted};
  cursor: pointer;
  padding: 0.3rem 0;

  input { accent-color: ${theme.colors.pink}; width: 15px; height: 15px; }
`;

const Code = styled.code`
  font-family: 'Consolas', monospace;
  font-size: 0.8rem;
  color: ${theme.colors.blue};
`;

const CoworkSettingsTab: React.FC<{ onSaved: () => void }> = ({ onSaved }) => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState<CoworkSettings | null>(null);
  const [commands, setCommands] = useState<CoworkCommandInfo[]>([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [openingCommand, setOpeningCommand] = useState('');
  const [customTriggers, setCustomTriggers] = useState<Record<string, string>>({});

  const loadCustomCommands = async () => {
    try {
      const { data } = await api.get('/commands');
      const map: Record<string, string> = {};
      (data || []).forEach((c: { _id: string; trigger: string }) => {
        if (c.trigger) map[c.trigger.toLowerCase()] = c._id;
      });
      setCustomTriggers(map);
    } catch {
      setCustomTriggers({});
    }
  };

  /**
   * Crea el comando propio con el texto por defecto (si todavía no existe) y lleva a Comandos,
   * que es donde se edita. Al existir, el de cowork queda reemplazado por ese.
   */
  const editInCommands = async (command: CoworkCommandInfo) => {
    const trigger = command.aliases[0];
    setOpeningCommand(command.id);
    setError('');
    try {
      if (!customTriggers[trigger]) {
        await api.post('/commands', {
          trigger,
          description: 'Ayuda de cowork (reemplaza al comando automático)',
          response: command.defaultResponse || '',
          action: 'static',
        });
      }
      navigate('/commands');
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudo abrir el comando en Comandos');
    } finally {
      setOpeningCommand('');
    }
  };

  /** Borra el comando propio para que vuelva a responder el de cowork, que se actualiza solo. */
  const restoreAutomatic = async (command: CoworkCommandInfo) => {
    const trigger = command.aliases[0];
    const id = customTriggers[trigger];
    if (!id) return;
    if (!window.confirm(`¿Borrar tu comando !${trigger} y volver al automático de cowork?`)) return;

    setOpeningCommand(command.id);
    setError('');
    try {
      await api.delete(`/commands/${id}`);
      await loadCustomCommands();
      setMessage(`!${trigger} vuelve a responder el texto automático`);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudo borrar el comando');
    } finally {
      setOpeningCommand('');
    }
  };

  useEffect(() => {
    api
      .get('/cowork/settings')
      .then(({ data }) => {
        setSettings(data.settings);
        setCommands(data.commands || []);
      })
      .catch(() => setError('No se pudo cargar la configuración'));
    loadCustomCommands();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error && !settings) return <ErrorText>{error}</ErrorText>;
  if (!settings) return <Muted>Cargando configuración…</Muted>;

  const patch = (changes: Partial<CoworkSettings>) => setSettings({ ...settings, ...changes });

  const save = async () => {
    setSaving(true);
    setMessage('');
    setError('');
    try {
      const { data } = await api.put('/cowork/settings', settings);
      setSettings(data.settings);
      setMessage('Configuración guardada.');
      onSaved();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };

  const numberField = (
    label: string,
    key: keyof CoworkSettings,
    hint?: string,
  ) => (
    <FormRow>
      <Label>{label}</Label>
      <Input
        type="number"
        value={String(settings[key] ?? '')}
        onChange={(e) => patch({ [key]: parseInt(e.target.value, 10) || 0 } as any)}
      />
      {hint && <Muted>{hint}</Muted>}
    </FormRow>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
      {message && <Toast>{message}</Toast>}
      {error && <ErrorText>{error}</ErrorText>}

      <Grid $cols={2} style={{ alignItems: 'start' }}>
        <Surface>
          <SurfaceHeader><SurfaceTitle>Pomodoro por defecto</SurfaceTitle></SurfaceHeader>
          <SurfaceBody>
            <FormGrid>
              {numberField('Minutos de focus', 'focusMinutes')}
              {numberField('Minutos de descanso', 'breakMinutes')}
              {numberField('Ciclos totales', 'totalCycles')}
              {numberField('Minutos de descanso largo', 'longBreakMinutes')}
              {numberField('Ciclos antes del descanso largo', 'cyclesBeforeLongBreak')}
            </FormGrid>

            <Check>
              <input
                type="checkbox"
                checked={settings.longBreakEnabled}
                onChange={(e) => patch({ longBreakEnabled: e.target.checked })}
              />
              Usar descanso largo
            </Check>
            <Check>
              <input
                type="checkbox"
                checked={settings.autoStartNext}
                onChange={(e) => patch({ autoStartNext: e.target.checked })}
              />
              Arrancar automáticamente el siguiente período
            </Check>
            <Muted>
              Estos valores se aplican al iniciar una sesión nueva. La sesión en curso no se toca.
            </Muted>
          </SurfaceBody>
        </Surface>

        <Surface>
          <SurfaceHeader><SurfaceTitle>Chat y comandos</SurfaceTitle></SurfaceHeader>
          <SurfaceBody>
            <Check>
              <input
                type="checkbox"
                checked={settings.enabled}
                onChange={(e) => patch({ enabled: e.target.checked })}
              />
              Cowork encendido
            </Check>
            <Muted>
              Apagado, ningún comando de cowork responde en el chat, ni a los viewers ni a Euge y los
              mods, y sin avisar nada. Es el mismo interruptor que está arriba en la cabecera.
            </Muted>
            <Check>
              <input
                type="checkbox"
                checked={settings.viewerCommandsEnabled}
                onChange={(e) => patch({ viewerCommandsEnabled: e.target.checked })}
              />
              Los viewers pueden usar los comandos de cowork
            </Check>
            <Check>
              <input
                type="checkbox"
                checked={settings.chatMessagesEnabled}
                onChange={(e) => patch({ chatMessagesEnabled: e.target.checked })}
              />
              Anunciar los cambios de fase en el chat
            </Check>
            <Check>
              <input
                type="checkbox"
                checked={settings.soundEnabled}
                onChange={(e) => patch({ soundEnabled: e.target.checked })}
              />
              Permitir sonido en los overlays
            </Check>
            <Muted>
              El sonido además hay que activarlo en cada overlay. Activalo en uno solo para que OBS no
              lo reproduzca duplicado.
            </Muted>

            <FormGrid style={{ marginTop: '0.85rem' }}>
              <FormRow>
                <Label>Máx. tareas activas por viewer</Label>
                <Input
                  type="number"
                  value={String(settings.limits.maxActiveTasksPerViewer)}
                  onChange={(e) => patch({
                    limits: { ...settings.limits, maxActiveTasksPerViewer: parseInt(e.target.value, 10) || 1 },
                  })}
                />
                <Muted>Cuántas puede cargar cada uno con !tarea. No es el máximo que se ve en los overlays.</Muted>
              </FormRow>
              <FormRow>
                <Label>Largo máximo del texto</Label>
                <Input
                  type="number"
                  value={String(settings.limits.maxTaskLength)}
                  onChange={(e) => patch({
                    limits: { ...settings.limits, maxTaskLength: parseInt(e.target.value, 10) || 20 },
                  })}
                />
              </FormRow>
              <FormRow>
                <Label>Cooldown por comando (seg.)</Label>
                <Input
                  type="number"
                  value={String(settings.limits.commandCooldownSeconds)}
                  onChange={(e) => patch({
                    limits: { ...settings.limits, commandCooldownSeconds: parseInt(e.target.value, 10) || 0 },
                  })}
                />
              </FormRow>
            </FormGrid>
          </SurfaceBody>
        </Surface>
      </Grid>

      <Surface>
        <SurfaceHeader><SurfaceTitle>Mensajes automáticos</SurfaceTitle></SurfaceHeader>
        <SurfaceBody>
          <FormGrid>
            <FormRow>
              <Label>Al comenzar el focus</Label>
              <TextArea
                value={settings.messages.focusStart}
                onChange={(e) => patch({ messages: { ...settings.messages, focusStart: e.target.value } })}
              />
            </FormRow>
            <FormRow>
              <Label>Al comenzar el descanso</Label>
              <TextArea
                value={settings.messages.breakStart}
                onChange={(e) => patch({ messages: { ...settings.messages, breakStart: e.target.value } })}
              />
            </FormRow>
            <FormRow>
              <Label>Al volver al focus</Label>
              <TextArea
                value={settings.messages.focusResume}
                onChange={(e) => patch({ messages: { ...settings.messages, focusResume: e.target.value } })}
              />
            </FormRow>
            <FormRow>
              <Label>Al finalizar la sesión</Label>
              <TextArea
                value={settings.messages.sessionEnd}
                onChange={(e) => patch({ messages: { ...settings.messages, sessionEnd: e.target.value } })}
              />
            </FormRow>
          </FormGrid>
          <Muted>
            Variables: <Code>{'{focusMinutes}'}</Code> <Code>{'{breakMinutes}'}</Code>{' '}
            <Code>{'{cycle}'}</Code> <Code>{'{totalCycles}'}</Code> <Code>{'{communityCompleted}'}</Code>
          </Muted>
        </SurfaceBody>
      </Surface>

      <div>
        <Button onClick={save} disabled={saving}>
          {saving ? 'Guardando…' : 'Guardar configuración'}
        </Button>
      </div>

      <Surface>
        <SurfaceHeader>
          <SurfaceTitle>Comandos de cowork</SurfaceTitle>
          <Muted style={{ margin: 0 }}>Un handler por acción, todos los alias hacen lo mismo</Muted>
        </SurfaceHeader>
        <SurfaceBody $flush>
          <TableWrap style={{ maxHeight: 340 }}>
            <Table>
              <thead>
                <tr>
                  <th>Acción</th>
                  <th>Uso</th>
                  <th>Alias</th>
                  <th>Permisos</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {commands.map((command) => {
                  const overridden = command.aliases.some((a) => customTriggers[a]);
                  return (
                    <tr key={command.id}>
                      <td style={{ color: '#fff' }}>{command.id}</td>
                      <td><Code>{command.usage}</Code></td>
                      <td>{command.aliases.map((a) => `!${a}`).join(' · ')}</td>
                      <td>{command.adminOnly ? 'Euge / mods para controlar' : 'Todos'}</td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {overridden && (
                          <Muted style={{ margin: '0 0 0.25rem', color: theme.colors.pinkSoft }}>
                            Responde tu comando de Comandos
                          </Muted>
                        )}
                        {command.defaultResponse && (
                          <Button
                            type="button"
                            $variant="ghost"
                            $size="sm"
                            disabled={openingCommand === command.id}
                            onClick={() => editInCommands(command)}
                          >
                            {openingCommand === command.id
                              ? '…'
                              : overridden
                                ? 'Editar en Comandos'
                                : 'Editar texto'}
                          </Button>
                        )}
                        {overridden && (
                          <Button
                            type="button"
                            $variant="ghost"
                            $size="sm"
                            disabled={openingCommand === command.id}
                            onClick={() => restoreAutomatic(command)}
                          >
                            Volver al automático
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </TableWrap>
          <div style={{ padding: '0.75rem 1rem' }}>
            <Muted>
              Si creás un comando propio con el mismo trigger en la sección Comandos, ese tiene prioridad
              y el de cowork queda deshabilitado para ese nombre. «Editar texto» hace justamente eso:
              copia el texto actual a Comandos para que lo escribas a gusto. Borrá el comando de ahí para
              volver al automático. Solo aparece en los de texto fijo, porque los demás calculan la
              respuesta con tus tareas.
            </Muted>
          </div>
        </SurfaceBody>
      </Surface>
    </div>
  );
};

export default CoworkSettingsTab;
